import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, ChevronDown } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const PIE_COLORS = ["#f59e0b", "#3b82f6", "#10b981", "#ec4899", "#8b5cf6", "#f97316", "#06b6d4", "#84cc16"];
const CHART_TOOLTIP_STYLE = { background: "#18181b", border: "1px solid #27272a", borderRadius: 4 };
const CHART_LABEL_STYLE = { color: "#fafafa", fontWeight: 600 };
const CHART_ITEM_STYLE = { color: "#fafafa" };

export function ClientRevenueSection({ clientData, totalRevenue, hasInvoices, open, onToggle }) {
  return (
    <Card className="border-border" data-testid="revenue-by-client-section">
      <CardHeader className="pb-2 cursor-pointer select-none hover:bg-secondary/20 transition-colors rounded-t-lg" onClick={onToggle} data-testid="client-revenue-toggle">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> Revenue by Client
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">{clientData.length} client{clientData.length !== 1 ? "s" : ""} with revenue</p>
          </div>
          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7" data-testid="revenue-by-client-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Client</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Revenue</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">{hasInvoices ? "Invoices" : "Events"}</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clientData.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No client data</TableCell></TableRow>}
                  {clientData.map((c, i) => (
                    <TableRow key={c.name} data-testid={`client-revenue-row-${i}`}>
                      <TableCell><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /><span className="font-medium text-sm">{c.name}</span></div></TableCell>
                      <TableCell className="text-right font-semibold text-sm text-primary">${c.revenue.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">{c.events}</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">{totalRevenue ? Math.round((c.revenue / totalRevenue) * 100) : 0}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="lg:col-span-5" data-testid="revenue-client-pie">
              {clientData.length > 0 && (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={clientData} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={80} strokeWidth={1} stroke="#09090b">
                        {clientData.map((_, i) => <Cell key={`client-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_LABEL_STYLE} itemStyle={CHART_ITEM_STYLE} formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 mt-2 justify-center">
                    {clientData.map((c, i) => (
                      <div key={c.name} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-muted-foreground">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
